import { Box, useTheme } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { UtilizationPointType } from '../../redux/slices/tools/toolsInterface';

// Row-height version of the utilization chart: same 12 months, same 0-100%
// scale so rows are comparable to each other, but no axes, labels or hover. The
// viewBox scales it to whatever cell it lands in, so nothing here measures the
// DOM.
const VIEW_W = 240;
const VIEW_H = 28;

type UtilizationSparklineProps = { points: UtilizationPointType[] };

export const UtilizationSparkline = ({ points }: UtilizationSparklineProps): ReactElement => {
  const theme = useTheme();

  const geometry = useMemo(() => {
    if (points.length < 2) {
      return undefined;
    }
    const x = (index: number) => (index / (points.length - 1)) * VIEW_W;
    // Inset by the stroke width so the line is not clipped at 0% or 100%.
    const y = (pct: number) => VIEW_H - 1.5 - (Math.min(Math.max(pct, 0), 100) / 100) * (VIEW_H - 3);
    const path = points.map((point, index) => `${index ? 'L' : 'M'}${x(index).toFixed(1)},${y(point.trackedPct).toFixed(1)}`).join(' ');
    return { path, area: `${path} L${VIEW_W},${VIEW_H} L0,${VIEW_H} Z` };
  }, [points]);

  return (
    <Box sx={{ width: '100%', minWidth: 120, height: 28, display: 'block' }}>
      {geometry && (
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden="true">
          <path d={geometry.area} fill={theme.palette.primary.main} fillOpacity={0.12} stroke="none" />
          <path
            d={geometry.path}
            fill="none"
            stroke={theme.palette.primary.main}
            strokeWidth={1}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
    </Box>
  );
};
